// src/pages/ResourceManagement.jsx
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const EMPTY = { scope: 'county', county: '', name: '', category: '', phone: '', description: '', url: '' };

export default function ResourceManagement() {
  const [resources, setResources] = useState([]);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    try {
      const body = await api('/resources/admin/all');
      setResources(body.resources);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api('/resources/admin', { method: 'POST', body: JSON.stringify(form) });
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(id) {
    if (!confirm('Delete this resource listing?')) return;
    try {
      await api(`/resources/admin/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleActive(r) {
    try {
      await api(`/resources/admin/${r.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: r.is_active ? 0 : 1 }) });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-charcoal">Resource Directory</h1>
          <p className="text-xs text-ink/50 mt-1">Feeds the public site's help directory. Unverified phone numbers show a caveat there automatically.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="rounded-md bg-ink text-white text-sm font-medium px-4 py-2 hover:bg-ink-700">+ Add resource</button>
      </div>

      {error && <p className="mt-3 text-sm text-p1">{error}</p>}

      {showForm && (
        <form onSubmit={submit} className="mt-4 rounded-lg border border-ink/10 bg-white p-4 grid grid-cols-2 gap-3">
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm">
            <option value="national">National</option>
            <option value="county">County</option>
          </select>
          <input placeholder="County (if county-scoped)" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm" />
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm col-span-2" />
          <input required placeholder="Category (e.g. shelter, legal_aid, hospital)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-md border border-ink/20 px-2.5 py-1.5 text-sm col-span-2" />
          <div className="col-span-2 flex gap-2">
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY); }} className="flex-1 text-xs text-ink/50 py-2">Cancel</button>
            <button type="submit" className="flex-1 rounded-md bg-sage-600 text-white text-xs font-medium py-2">Save</button>
          </div>
        </form>
      )}

      <div className="mt-5 rounded-lg border border-ink/10 bg-white overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-ink/5 text-left uppercase tracking-wide text-ink/50">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Scope</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {resources.map((r) => (
              <tr key={r.id} className={`border-t border-ink/5 ${!r.is_active ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">{r.scope === 'county' ? r.county : 'National'}</td>
                <td className="px-3 py-2">{r.category}</td>
                <td className="px-3 py-2 font-mono">{r.phone ?? '—'}</td>
                <td className="px-3 py-2">{r.is_active ? 'Active' : 'Hidden'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => toggleActive(r)} className="text-accent hover:underline mr-3">{r.is_active ? 'Hide' : 'Show'}</button>
                  <button onClick={() => remove(r.id)} className="text-p1 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
            {resources.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-ink/40">No resources yet — using the built-in fallback list on the public site.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
